/**
 * GenerationContext -- backward-compatible facade
 *
 * All heavy lifting (jobs Map, polling, server communication) now lives in
 * GenerationJobsContext.  This file re-exports a useGeneration() hook with
 * the same shape that HomePage, GeneratingPage, and GenerationQueue already
 * consume, so those files need zero or minimal changes.
 *
 * Legacy fields (isGenerating, progress, message, error, result, elapsedTime)
 * are derived from the active job in the jobs Map.
 */

import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  GenerationJobsProvider,
  useGenerationJobs,
} from './GenerationJobsContext';
import type { FormData, GenerateResponse } from '../types';

// Re-export the GenerationState interface for any code that imports it directly
export interface GenerationState {
  isGenerating: boolean;
  progress: number;
  message: string;
  error: string | null;
  result: GenerateResponse | null;
  elapsedTime: number;
}

// The full context type that consumers destructure from useGeneration()
export interface GenerationContextType extends GenerationState {
  generate: (formData: FormData) => Promise<void>;
  cancel: () => void;
  reset: () => void;
  jobs: Map<string, import('../types').GenerationJob>;
  activeJobId: string | null;
  startGeneration: (formData: FormData) => Promise<string>;
  getJob: (jobId: string) => import('../types').GenerationJob | undefined;
  removeJob: (jobId: string) => void;
  cancelJob: (jobId: string) => void;
  setActiveJob: (jobId: string | null) => void;
  hasActiveJobs: boolean;
  isLoadingJobs: boolean;
  isLoadingMore: boolean;
  hasMoreJobs: boolean;
  totalJobs: number;
  loadMoreJobs: () => Promise<void>;
}

/**
 * Provider -- wraps the real GenerationJobsProvider.
 * Place this inside <BrowserRouter> (it needs useNavigate) and inside
 * <QueryClientProvider> (the jobs context uses TanStack Query).
 */
export const GenerationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return <GenerationJobsProvider>{children}</GenerationJobsProvider>;
};

/**
 * Hook -- backward-compatible with all existing consumers.
 *
 * Derives the legacy scalar fields (isGenerating, progress, ...) from the
 * active job, so components that only read those scalars do not need to change.
 */
export function useGeneration(): GenerationContextType {
  const jobsCtx = useGenerationJobs();
  const navigate = useNavigate();

  const {
    jobs,
    activeJobId,
    isLoadingJobs,
    isLoadingMore,
    hasActiveJobs,
    hasMoreJobs,
    totalJobs,
    startGeneration: rawStartGeneration,
    getJob,
    removeJob,
    cancelJob,
    setActiveJob,
    loadMoreJobs,
  } = jobsCtx;

  // Derive legacy scalar state from the active job
  const activeJob = activeJobId ? jobs.get(activeJobId) : undefined;
  const isGenerating = activeJob?.status === 'generating';
  const progress = activeJob?.progress ?? 0;
  const message = activeJob?.message ?? '';
  const error = activeJob?.error ?? null;
  const result = activeJob?.result ?? null;
  const elapsedTime = activeJob
    ? Math.floor((Date.now() - activeJob.startedAt) / 1000)
    : 0;

  // Legacy generate: starts generation + navigates to /generating/:sessionId
  // Using replace: true so GeneratingPage doesn't stay in history when
  // user navigates back from PreviewPage
  const generate = useCallback(
    async (formData: FormData) => {
      const sessionId = await rawStartGeneration(formData);
      navigate(`/generating/${sessionId}`, { replace: true });
    },
    [rawStartGeneration, navigate],
  );

  // Legacy cancel: cancels active job + navigates home
  const cancel = useCallback(() => {
    if (activeJobId) {
      cancelJob(activeJobId);
    }
    navigate('/');
  }, [activeJobId, cancelJob, navigate]);

  // Legacy reset: clears active job id so the form is usable again.
  // Does NOT cancel running jobs -- they continue in the background.
  const reset = useCallback(() => {
    setActiveJob(null);
  }, [setActiveJob]);

  return {
    // Legacy scalars
    isGenerating,
    progress,
    message,
    error,
    result,
    elapsedTime,
    // Legacy methods
    generate,
    cancel,
    reset,
    // Multi-job API
    jobs,
    activeJobId,
    startGeneration: rawStartGeneration,
    getJob,
    removeJob,
    cancelJob,
    setActiveJob,
    hasActiveJobs,
    isLoadingJobs,
    isLoadingMore,
    hasMoreJobs,
    totalJobs,
    loadMoreJobs,
  };
}

export default useGeneration;
